"use client";

/**
 * Input pane for the /compare page.
 *
 * Up to 4 slots, each one of:
 *   - a URL text field (paste a bina.az / emlak.az listing link), or
 *   - a history-picked entry (one tap from a modal showing recent
 *     analyses the signed-in user has already run).
 *
 * The pane owns its local slot list; the parent receives the final
 * list via `onSubmit` when the user hits "Compare". We don't
 * auto-submit - a run is real compute, and the user should confirm
 * the final set first.
 *
 * Max 4 is enforced here (disables "+Add URL") AND on the backend
 * (rejects requests with more than 4 items). Min 2 is enforced on
 * submit (button disabled until two slots are filled).
 *
 * HistoryPickerModal lazy-loads recent entries from /api/history
 * using the signed-in user's existing session. Anonymous users see
 * a "sign in to pick from history" note instead.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getHistory, getHistoryDetail, validateCompareUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useT, useLocale } from "@/lib/locale-context";
import { translateVerdict } from "@/lib/i18n";
import type { CompareSlot, HistoryItem } from "@/types";
import { canonicalListingKey } from "@/lib/listing-url";
import { colorForIndex } from "./palette";
import { Link2, History, X, Plus, Loader2, Check, AlertTriangle, Zap, ExternalLink } from "lucide-react";
import UrlPreviewCard from "@/components/UrlPreviewCard";
import Photo from "@/components/Photo";

interface Props {
  slots: CompareSlot[];
  onSlotsChange: (slots: CompareSlot[]) => void;
  onSubmit: () => void;
  loading: boolean;
  /** When true, suppress the footer hint line ("min listings") so the
   *  parent can render it in its own position (e.g. below the
   *  user-intent textarea instead of above it). The hint is still
   *  produced by the same predicate - this just hides the in-component
   *  version. */
  hideFooterHints?: boolean;
  /** External gate - e.g. the optional user-intent hint exceeding its
   *  character cap lives on a sibling component, and we need the
   *  Compare button to stay disabled until the user trims it down. */
  externalDisabled?: boolean;
  /** When true, the internal submit button is hidden and the parent
   *  renders its own button elsewhere (below a sibling input, for
   *  example). `onStateChange` keeps the parent in sync with
   *  canSubmit so its button can reflect the same gating. */
  hideInternalSubmit?: boolean;
  /** Fires whenever canSubmit changes - lets the parent render its own
   *  submit button with the matching enabled state. */
  onStateChange?: (state: { canSubmit: boolean }) => void;
}

const MAX_SLOTS = 4;
const MIN_SLOTS_TO_SUBMIT = 2;

function newUrlSlot(): CompareSlot {
  // crypto.randomUUID() is available in all targeted browsers (Safari
  // 15.4+, Chrome 92+) and on Node 19+; the ||-fallback is a cheap
  // guard for legacy SSR paths without node crypto in scope.
  const uuid =
    (typeof crypto !== "undefined" && "randomUUID" in crypto)
      ? crypto.randomUUID()
      : `slot_${Math.random().toString(36).slice(2, 10)}`;
  return { id: uuid, kind: "url", value: "" };
}

export default function CompareInput({
  slots, onSlotsChange, onSubmit, loading,
  externalDisabled = false, hideInternalSubmit = false,
  hideFooterHints = false, onStateChange,
}: Props) {
  const t = useT();
  const { locale } = useLocale();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTargetSlotId, setPickerTargetSlotId] = useState<string | null>(null);

  const updateSlot = (id: string, patch: Partial<CompareSlot>) => {
    onSlotsChange(slots.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  // ── URL validation ──────────────────────────────────────────────
  //
  // Each URL slot gets a debounced 500ms validate call. The in-flight
  // abort controller is keyed by slot.id so edits within the debounce
  // window cancel the prior request cleanly. `validatedValues` tracks
  // "what value did we last validate for this slot id" so we skip
  // duplicate calls when React re-renders without a content change
  // (e.g. a neighbour slot updating its validation status).
  const abortersRef = useRef<Map<string, AbortController>>(new Map());
  const validatedValuesRef = useRef<Map<string, string>>(new Map());
  // Latest slots in a ref so the debounce timer always reads current
  // state at fire time (useEffect closure captures stale props
  // otherwise).
  const slotsRef = useRef(slots);
  useEffect(() => { slotsRef.current = slots; }, [slots]);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (const slot of slots) {
      if (slot.kind !== "url") continue;
      const url = slot.value.trim();
      // Empty input → reset validation to idle.
      if (!url) {
        if (slot.validation && slot.validation.status !== "idle") {
          updateSlot(slot.id, { validation: { status: "idle" } });
        }
        validatedValuesRef.current.delete(slot.id);
        continue;
      }
      // Already validated this exact value for this slot → no-op.
      if (validatedValuesRef.current.get(slot.id) === url) continue;

      // Client-side: if it doesn't even look like a supported listing
      // URL, flip to invalid immediately without a network call.
      const clientKey = canonicalListingKey(url);
      if (!clientKey) {
        if (slot.validation?.reason !== "unsupported") {
          updateSlot(slot.id, {
            validation: {
              status: "invalid",
              reason: "unsupported",
              message: t("compare.input.validation.unsupported"),
            },
          });
        }
        validatedValuesRef.current.set(slot.id, url);
        continue;
      }

      // Server-side debounce - 500 ms after last keystroke.
      // Mark "checking" immediately so the spinner appears.
      if (slot.validation?.status !== "checking") {
        updateSlot(slot.id, {
          validation: { status: "checking", canonical: clientKey },
        });
      }
      const tid = setTimeout(async () => {
        // Abort any prior in-flight for this slot.
        abortersRef.current.get(slot.id)?.abort();
        const ac = new AbortController();
        abortersRef.current.set(slot.id, ac);
        try {
          const result = await validateCompareUrl(url, { signal: ac.signal });
          // Check the slot still exists + value didn't drift while we
          // waited (user deleted / edited further).
          const current = slotsRef.current.find((s) => s.id === slot.id);
          if (!current || current.kind !== "url" || current.value.trim() !== url) return;
          validatedValuesRef.current.set(slot.id, url);
          updateSlot(slot.id, {
            validation: {
              status: result.valid ? "valid" : "invalid",
              canonical: result.canonical ?? clientKey,
              reason: result.reason ?? null,
              message: result.message ?? null,
              preview: result.valid ? {
                price: result.price ?? null,
                area_m2: result.area_m2 ?? null,
                room_count: result.room_count ?? null,
                location_name: result.location_name ?? null,
                mode: result.mode ?? null,
              } : undefined,
            },
          });
        } catch (err) {
          // Network error / 429 / aborted. If aborted (replaced by a
          // newer keystroke), leave the slot untouched.
          if ((err as { name?: string } | null)?.name === "AbortError") return;
          // Other errors: mark idle so the user can try again by
          // editing. Backend submit would catch real failures anyway.
          const current = slotsRef.current.find((s) => s.id === slot.id);
          if (!current) return;
          updateSlot(slot.id, { validation: { status: "idle" } });
        }
      }, 500);
      timers.push(tid);
    }
    return () => {
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots.map((s) => `${s.id}:${s.kind}:${s.value}`).join("|")]);

  // ── Canonical key per slot (for dedup) ─────────────────────────
  //
  // For URL slots we prefer the server-confirmed canonical (guaranteed
  // to match what the backend will canonicalize on submit), falling
  // back to the client-side regex mirror while validation is pending.
  // For history slots we derive the key from the row's listing_url
  // - also kept by the frontend alongside the picker preview.
  const canonicalBySlotId = useMemo(() => {
    const out = new Map<string, string>();
    for (const s of slots) {
      let key: string | null = null;
      if (s.kind === "url") {
        key = s.validation?.canonical ?? canonicalListingKey(s.value);
      } else if (s.historyPreview?.listing_url) {
        key = canonicalListingKey(s.historyPreview.listing_url);
      }
      if (key) out.set(s.id, key);
    }
    return out;
  }, [slots]);

  // Slot IDs whose canonical key collides with an *earlier* slot in
  // the list. Only the duplicates (second+ occurrence) get the red
  // banner - the first occurrence is considered the "canonical" slot
  // and stays clean.
  const duplicateSlotIds = useMemo(() => {
    const seen = new Map<string, string>();      // key → first slot id
    const dupes = new Set<string>();
    for (const s of slots) {
      const key = canonicalBySlotId.get(s.id);
      if (!key) continue;
      if (seen.has(key)) dupes.add(s.id);
      else seen.set(key, s.id);
    }
    return dupes;
  }, [slots, canonicalBySlotId]);

  // ── Sale vs rent consistency ──────────────────────────────────────
  //
  // Comparing a rental against a sale is nonsensical - the prices
  // live on totally different scales (~1,500 AZN/month vs ~300,000 AZN
  // sticker), the verdicts are computed against different models, and
  // the SHAP buckets diverge. The FIRST filled slot with a known mode
  // locks the comparison; any later slot whose mode differs gets
  // flagged and submit is blocked until the user removes or swaps it.
  const modeBySlotId = useMemo(() => {
    const out = new Map<string, "sale" | "rent">();
    for (const s of slots) {
      let mode: "sale" | "rent" | null | undefined = null;
      if (s.kind === "url") {
        // Only trust a *validated* URL's mode - checking / idle don't
        // have a reliable answer yet.
        if (s.validation?.status === "valid") {
          mode = s.validation.preview?.mode ?? null;
        }
      } else if (s.historyPreview) {
        mode = s.historyPreview.mode ?? null;
      }
      if (mode === "sale" || mode === "rent") out.set(s.id, mode);
    }
    return out;
  }, [slots]);

  // The locked mode is whichever mode the earliest slot-in-order
  // resolved to. Returning null means the comparison hasn't locked
  // yet (no slot has a known mode) so no mismatch can be computed.
  const lockedMode: "sale" | "rent" | null = useMemo(() => {
    for (const s of slots) {
      const m = modeBySlotId.get(s.id);
      if (m) return m;
    }
    return null;
  }, [slots, modeBySlotId]);

  const mismatchSlotIds = useMemo(() => {
    const out = new Set<string>();
    if (!lockedMode) return out;
    for (const s of slots) {
      const m = modeBySlotId.get(s.id);
      if (m && m !== lockedMode) out.add(s.id);
    }
    return out;
  }, [slots, modeBySlotId, lockedMode]);
  const removeSlot = (id: string) => {
    // Never drop below 1 slot - the pane shouldn't collapse into nothing.
    if (slots.length <= 1) return;
    onSlotsChange(slots.filter((s) => s.id !== id));
  };
  const addSlot = () => {
    if (slots.length >= MAX_SLOTS) return;
    onSlotsChange([...slots, newUrlSlot()]);
  };

  const openPicker = (slotId: string) => {
    setPickerTargetSlotId(slotId);
    setPickerOpen(true);
  };

  // A slot that arrived as a history id in the URL (the result page's
  // "compare" link) has no preview yet: read the row and show it the
  // way a picked one is shown (owner, 2026-10-11: the slot read as
  // "not picked yet" and nothing fit).
  const pendingHistory = slots.filter((s) => s.kind === "history_id" && s.value && !s.historyPreview).map((s) => `${s.id}:${s.value}`).join("|");
  useEffect(() => {
    if (!pendingHistory) return;
    let alive = true;
    for (const part of pendingHistory.split("|")) {
      const [slotId, value] = part.split(":");
      const id = Number(value);
      if (!Number.isFinite(id)) continue;
      getHistoryDetail(id).then((d) => {
        if (!alive) return;
        const r = d.result;
        updateSlot(slotId, { historyPreview: {
          id, verdict: r.verdict ?? null, price: r.price ?? null, location_name: r.location_name ?? null,
          area_m2: r.area_m2 ?? null, room_count: r.room_count ?? null, mode: r.analysis_mode ?? null, listing_url: r.url ?? null,
        } });
      }).catch(() => {});
    }
    return () => { alive = false; };
    // updateSlot is stable enough; the key below is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingHistory]);

  const handlePicked = (item: HistoryItem) => {
    if (!pickerTargetSlotId) return;
    // Reject duplicates - adding the same history row twice would
    // violate the user's intent (they want N *distinct* apartments).
    const duplicate = slots.some(
      (s) => s.id !== pickerTargetSlotId && s.kind === "history_id" && s.value === String(item.id),
    );
    if (duplicate) {
      setPickerOpen(false);
      return;
    }
    updateSlot(pickerTargetSlotId, {
      kind: "history_id",
      value: String(item.id),
      historyPreview: {
        id: item.id,
        verdict: item.verdict ?? null,
        price: item.price ?? null,
        location_name: item.location_name ?? null,
        area_m2: item.area_m2 ?? null,
        room_count: item.room_count ?? null,
        // Sale vs rent - threaded through so the mode-mismatch guard
        // catches "user picks a rental from history alongside a sale
        // URL" before submit.
        mode: item.mode ?? null,
        // Carry the source URL so the dedup logic can derive a
        // canonical key and catch the "user picks from history + also
        // pastes the URL for the same listing" case visually.
        listing_url: item.listing_url ?? null,
      },
      validation: undefined,  // fresh pick clears any URL-side state
    });
    setPickerOpen(false);
    setPickerTargetSlotId(null);
  };

  const { user } = useAuth();

  const filledSlots = slots.filter(
    (s) => (s.kind === "url" && s.value.trim().length > 0) || (s.kind === "history_id" && s.value),
  );
  // No per-URL cost chip any more: a comparison costs the person
  // nothing on any account (owner, 2026-10-18).
  // Any filled URL slot currently marked invalid blocks submit - the
  // backend would just error out on it and it's cheaper to tell the
  // user up front. "checking" and "idle" are allowed through (backend
  // re-validates on submit); only hard "invalid" is a hard block.
  const hasInvalidSlot = slots.some(
    (s) => s.kind === "url" && s.value.trim().length > 0 && s.validation?.status === "invalid",
  );
  const hasDuplicates = duplicateSlotIds.size > 0;
  const hasModeMismatch = mismatchSlotIds.size > 0;
  const canSubmit =
    filledSlots.length >= MIN_SLOTS_TO_SUBMIT &&
    !loading && !hasInvalidSlot && !hasDuplicates && !hasModeMismatch &&
    !externalDisabled;

  // Keep parent in sync so its external submit button can enable/disable.
  // Sends on every render where the value changed; React batches
  // adjacent identical calls.
  const stateRef = useRef({ canSubmit });
  useEffect(() => {
    if (!onStateChange) return;
    if (stateRef.current.canSubmit !== canSubmit) {
      stateRef.current = { canSubmit };
      onStateChange({ canSubmit });
    }
  }, [canSubmit, onStateChange]);

  // Root is a <div> (not <form>) - the parent page wraps its own
  // <form> around us so the submit button can sit under a sibling
  // (the user-intent textarea). Nested forms are invalid HTML, and
  // since the parent owns the form, Enter-to-submit already works
  // without us needing our own form wrapper.
  return (
    <div className="space-y-3">
      {/* gap-2 on mobile keeps the slot rows visually adjacent (their
          own card chrome + ring already do the separation work) -
          the page-level form wrapper no longer adds an outer card on
          phones, so the extra gap-3 read as wasted whitespace. */}
      <div className="grid grid-cols-1 gap-2 sm:gap-3">
        {slots.map((slot, idx) => {
          const c = colorForIndex(idx);
          const isDuplicate = duplicateSlotIds.has(slot.id);
          const isModeMismatch = mismatchSlotIds.has(slot.id);
          const vstatus = slot.kind === "url" ? slot.validation?.status : undefined;
          // Visual status priority: mode-mismatch > duplicate > invalid >
          // valid > checking > idle. Mode-mismatch is the heaviest gate
          // because the user can't fix it by re-validating - they have
          // to swap the listing entirely.
          let rowRing = "ring-transparent";
          if (isModeMismatch) rowRing = "ring-rose-200";
          else if (isDuplicate) rowRing = "ring-rose-200";
          else if (vstatus === "invalid") rowRing = "ring-rose-200";
          else if (vstatus === "valid") rowRing = "ring-emerald-200";
          return (
            <div
              key={slot.id}
              className={`relative rounded-xl border border-gray-200 bg-white p-3 sm:p-4 ring-1 ${rowRing} hover:${c.ring} transition-all`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`inline-flex items-center gap-2 text-xs font-semibold ${c.label}`}>
                  <span className={`w-2 h-2 rounded-full ${c.dot}`} aria-hidden />
                  {t("compare.input.slot_label").replace("{n}", String(idx + 1))}
                </span>
                {slots.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSlot(slot.id)}
                    aria-label={t("compare.input.remove_slot")}
                    className="text-gray-400 hover:text-rose-600 transition-colors p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {slot.kind === "url" ? (
                <div>
                  {/* Live preview - debounces ~450 ms after typing /
                      pasting and surfaces a thumbnail + price + room
                      summary once the URL resolves. Same component the
                      homepage hero + /analyze use, so the user gets a
                      visually consistent "this is the listing I'm
                      about to compare" confirmation. Collapses to 0
                      height when the input is empty / unrecognised. */}
                  <UrlPreviewCard url={slot.value} variant="form" sourceLabel="found" />
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                      <Link2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={slot.value}
                        onChange={(e) => updateSlot(slot.id, { value: e.target.value })}
                        placeholder={t("compare.input.paste_url")}
                        className="w-full pl-9 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                        disabled={loading}
                        suppressHydrationWarning
                      />
                      {/* Inline validation status indicator - right of the
                          input, vertically centred. Spinner while checking,
                          green check when the listing resolved cleanly, red
                          triangle with tooltip text on failure. */}
                      {vstatus === "checking" && (
                        <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" aria-label={t("compare.input.validation.checking")} />
                      )}
                      {vstatus === "valid" && !isDuplicate && (
                        <Check className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600" aria-label={t("compare.input.validation.valid")} />
                      )}
                      {(vstatus === "invalid" || isDuplicate) && (
                        <AlertTriangle className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-rose-600" aria-label={isDuplicate ? t("compare.input.duplicate_listing") : (slot.validation?.message ?? "")} />
                      )}
                    </div>
                    {/* Anonymous users have no history to pick from -
                        clicking this button only ever surfaced a
                        "sign in" message inside the modal, which was
                        a dead-end for a casual visitor. Hide entirely
                        until the user is signed in; signed-in users
                        see the same affordance as before. */}
                    {user && (
                      <button
                        type="button"
                        onClick={() => openPicker(slot.id)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                        disabled={loading}
                      >
                        <History className="w-3.5 h-3.5" />
                        {t("compare.input.add_from_history")}
                      </button>
                    )}
                  </div>
                  {/* Inline message row below the input. Only renders
                      when there's something to say (validation error,
                      duplicate warning, or valid-preview chip). The
                      previous min-h-[1rem] placeholder ate ~20px
                      below every empty input - real estate that
                      added up across 2-4 slots on mobile. The minor
                      layout shift when validation appears is
                      acceptable for the savings. Duplicate takes
                      priority over validation error so the user sees
                      the actionable instruction first. */}
                  {/* Inline message row - errors / warnings ONLY now
                      that UrlPreviewCard renders the "valid" preview
                      richly above the input. Mode-mismatch +
                      duplicate + hard-invalid still need their text
                      callout under the input so the user sees what's
                      blocking submit. */}
                  {(() => {
                    const hasModeError = isModeMismatch;
                    const hasDuplicate = !hasModeError && isDuplicate;
                    const hasInvalid =
                      !hasModeError && !hasDuplicate
                      && vstatus === "invalid" && !!slot.validation?.message;
                    if (!hasModeError && !hasDuplicate && !hasInvalid) {
                      return null;
                    }
                    return (
                      <div className="mt-1.5 text-[11px] leading-4 truncate">
                        {hasModeError ? (
                          <span className="text-rose-600">
                            {t("compare.input.validation.mode_mismatch")
                              .replace("{mode}", lockedMode === "rent"
                                ? t("compare.input.validation.mode.rent")
                                : t("compare.input.validation.mode.sale"))}
                          </span>
                        ) : hasDuplicate ? (
                          <span className="text-rose-600">
                            {t("compare.input.duplicate_listing")}
                          </span>
                        ) : (
                          <span className="text-rose-600">
                            {slot.validation!.message}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div>
                  {/* Rich preview for the history-picked listing -
                      same UrlPreviewCard the URL slots show but with
                      sourceLabel="history" so the header chip reads
                      "From your history" (grey + History icon)
                      instead of the green "Listing found" used for
                      fresh pastes. Listings whose URL was lost from
                      history (older rows) fall back to the compact
                      summary below. */}
                  {slot.historyPreview?.listing_url ? (
                    <UrlPreviewCard
                      url={slot.historyPreview.listing_url}
                      variant="form"
                      sourceLabel="history"
                    />
                  ) : (
                    <div className="flex items-center gap-3 min-h-[2.625rem]">
                      <History className="w-4 h-4 text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {slot.historyPreview?.location_name || `#${slot.value}`}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {slot.historyPreview?.verdict
                            ? translateVerdict(slot.historyPreview.verdict, locale)
                            : t("compare.input.empty_slot")}
                          {slot.historyPreview?.price != null
                            ? ` · ${Math.round(slot.historyPreview.price).toLocaleString()} ${
                                slot.historyPreview.mode === "rent"
                                  ? t("result.permonth")
                                  : "AZN"
                              }`
                            : ""}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-end mt-2">
                    <button
                      type="button"
                      onClick={() => updateSlot(slot.id, {
                        kind: "url", value: "", historyPreview: undefined,
                      })}
                      className="text-xs text-gray-500 hover:text-gray-800 transition-colors"
                    >
                      {t("compare.input.switch_to_url")}
                    </button>
                  </div>
                  {/* Mode-mismatch / duplicate warnings - same gating
                      copy as the URL branch so both slot kinds surface
                      the same blockers in the same place. */}
                  {(isModeMismatch || isDuplicate) && (
                    <div className="mt-1.5 text-[11px] leading-4 truncate">
                      {isModeMismatch ? (
                        <span className="text-rose-600">
                          {t("compare.input.validation.mode_mismatch")
                            .replace("{mode}", lockedMode === "rent"
                              ? t("compare.input.validation.mode.rent")
                              : t("compare.input.validation.mode.sale"))}
                        </span>
                      ) : (
                        <span className="text-rose-600">
                          {t("compare.input.duplicate_listing")}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <button
          type="button"
          onClick={addSlot}
          disabled={loading || slots.length >= MAX_SLOTS}
          className="inline-flex items-center justify-center gap-1.5 text-sm text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900 font-medium disabled:text-gray-300 disabled:no-underline disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t("compare.input.add_url")}
        </button>

        {/* Internal submit button is hidden when the parent renders
            its own (typically under the user-intent textarea so the
            page reads top-to-bottom: listings → intent → submit). */}
        {!hideInternalSubmit && (
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-semibold text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {t("compare.input.loading")}
              </>
            ) : (
              t("compare.input.submit")
            )}
          </button>
        )}
      </div>

      {/* The sale/rent lock is explained per slot (mismatch message);
          the banner that restated it was removed 2026-09-16 (owner). */}
      {slots.length >= MAX_SLOTS && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          {t("compare.input.max_listings")}
        </p>
      )}
      {/* Goal-gradient strip: once anything is filled, show how close
          the comparison is to runnable (2 = the finish line, extra
          slots are bonus). A half-full bar + "1 left" moves people;
          "add minimum 2" reads like standing still. */}
      {filledSlots.length > 0 && (
        <div className="flex items-center gap-3">
          <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                filledSlots.length >= MIN_SLOTS_TO_SUBMIT ? "bg-emerald-500" : "bg-gray-900"
              }`}
              style={{ width: `${Math.min(filledSlots.length / MIN_SLOTS_TO_SUBMIT, 1) * 100}%` }}
            />
          </div>
          <span className={`text-xs font-semibold tabular-nums shrink-0 ${
            filledSlots.length >= MIN_SLOTS_TO_SUBMIT ? "text-emerald-600" : "text-gray-500"
          }`}>
            {filledSlots.length >= MIN_SLOTS_TO_SUBMIT
              ? t("compare.progress.ready")
              : t("compare.progress.one_more")}
          </span>
        </div>
      )}
      {!hideFooterHints && slots.length > 0 && filledSlots.length === 0 && (
        <p className="text-xs text-gray-500">{t("compare.input.min_listings")}</p>
      )}

      {pickerOpen && (
        <HistoryPickerModal
          onClose={() => setPickerOpen(false)}
          onPick={handlePicked}
          alreadyPickedIds={slots
            .filter((s) => s.kind === "history_id" && s.id !== pickerTargetSlotId)
            .map((s) => Number(s.value))
            .filter((n) => !Number.isNaN(n))}
          lockedMode={lockedMode}
        />
      )}
    </div>
  );
}


// ── History picker ────────────────────────────────────────────────

interface HistoryPickerProps {
  onClose: () => void;
  onPick: (item: HistoryItem) => void;
  alreadyPickedIds: number[];
  /** When a prior slot has already established the comparison's
   *  transaction type (sale / rent), the picker filters out history
   *  rows of the other kind so the user can't accidentally mix them. */
  lockedMode?: "sale" | "rent" | null;
}

function HistoryPickerModal({
  onClose, onPick, alreadyPickedIds, lockedMode,
}: HistoryPickerProps) {
  const t = useT();
  const { locale } = useLocale();
  const { user } = useAuth();
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // First page only - 25 most recent. A power user with hundreds of
  // analyses can still scroll the full history page and paste the
  // URL; the picker is optimised for the common case (recent run).
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setLoading(false);
      setError("signin");
      return;
    }
    getHistory(1, { source: "analyze" })
      .then((data) => {
        if (cancelled) return;
        setItems(data.items || []);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "error";
        setError(msg.toLowerCase().includes("sign") ? "signin" : "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      // z above the mobile tab bar (z-[1100]) - at z-50 the fixed
      // bottom nav rendered on top of this centered modal and covered
      // its lowest selectable history rows, so they couldn't be tapped
      // on mobile. 1200 sits above the nav
      // and the More/install sheets (1110).
      // PORTALED to body: the compare template's .page-enter animation
      // makes it a stacking context, so rendered in place this whole
      // overlay sat BELOW the persistent CompareNav pills outside it.
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4 modal-backdrop-enter"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-2xl bg-white shadow-xl flex flex-col max-h-[80vh] modal-enter"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            {t("compare.input.history_picker.title")}
          </h2>
          <button
            onClick={onClose}
            aria-label={t("compare.input.history_picker.close")}
            className="text-gray-500 hover:text-gray-800 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {loading && (
            <div className="p-8 text-center text-sm text-gray-400">
              {t("compare.summary.skeleton")}
            </div>
          )}
          {!loading && error === "signin" && (
            <div className="p-8 text-center text-sm text-amber-700 bg-amber-50 rounded-xl">
              {t("compare.input.history_picker.signin")}
            </div>
          )}
          {!loading && error === "error" && (
            <div className="p-8 text-center text-sm text-rose-600">
              {t("compare.error.generic")}
            </div>
          )}
          {(() => {
            // When the comparison is locked to a mode, filter out
            // history entries whose mode disagrees. "analyze" rows
            // without an explicit mode are treated as sale (the
            // backend's default for legacy cached entries).
            const displayItems = lockedMode
              ? items.filter((it) => (it.mode ?? "sale") === lockedMode)
              : items;
            const filteredOut = items.length - displayItems.length;
            return (
              <>
                {!loading && !error && lockedMode && filteredOut > 0 && (
                  <div className="mx-1 mb-2 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                    {t("compare.input.history_picker.mode_filtered")}
                  </div>
                )}
                {!loading && !error && displayItems.length === 0 && (
                  <div className="p-8 text-center text-sm text-gray-400">
                    {t("compare.input.history_picker.empty")}
                  </div>
                )}
                {!loading && !error && displayItems.length > 0 && (
                  <ul className="divide-y divide-gray-100">
                    {displayItems.map((it) => {
                      const alreadyPicked = alreadyPickedIds.includes(it.id);
                return (
                  <li key={it.id} className="flex items-stretch hover:bg-gray-50">
                    <button
                      type="button"
                      onClick={() => !alreadyPicked && onPick(it)}
                      disabled={alreadyPicked}
                      className="flex-1 text-left px-3 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed min-w-0"
                    >
                      <div className="flex items-center gap-3">
                        {/* Cover photo - without it the rows were just
                            near-identical location strings; the photo is
                            what actually identifies a listing. */}
                        {it.thumbnail_url ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <Photo src={it.thumbnail_url} alt="" frame="w-16 h-12 rounded-lg shrink-0" />
                        ) : (
                          <div className="w-16 h-12 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                            <History className="w-4 h-4 text-gray-300" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2 min-w-0">
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {it.location_name || `#${it.id}`}
                            </span>
                            {it.price != null && (
                              <span className="text-sm font-semibold text-gray-700 tabular-nums shrink-0">
                                {Math.round(it.price).toLocaleString()}{" "}
                                <span className="text-[10px] font-normal text-gray-400">
                                  {it.mode === "rent" ? t("result.permonth") : "AZN"}
                                </span>
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {[
                              it.verdict ? translateVerdict(it.verdict, locale) : null,
                              it.area_m2 != null ? `${it.area_m2} m²` : null,
                              it.room_count != null ? `${it.room_count} ${t("district.rooms.suffix")}` : null,
                            ].filter(Boolean).join(" · ") || "-"}
                          </div>
                        </div>
                        {alreadyPicked && (
                          <span className="text-[10px] font-semibold text-amber-700 uppercase shrink-0">
                            {t("compare.input.duplicate")}
                          </span>
                        )}
                      </div>
                    </button>
                    {/* Preview link to the original listing. Lives
                        outside the picker <button> so nested
                        interactive elements don't fight for the
                        click. New tab so the picker modal isn't
                        dismissed while the user takes a peek. */}
                    {it.listing_url && (
                      <a
                        href={it.listing_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title={t("compare.input.history_picker.open_source")}
                        className="shrink-0 px-3 flex items-center text-gray-400 hover:text-gray-900 border-l border-gray-100"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </li>
                );
              })}
                  </ul>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>,
    document.body,
  );
}
